import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Zap, Crown, Loader2 } from 'lucide-react';
import { getUserId } from '../../utils/userId';
import { useUIStore } from '../../store';

interface CreemPaymentButtonProps {
  plan: 'pro' | 'premium';
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

// 产品配置
const CREEM_CONFIG = {
  baseUrl: 'https://www.creem.io', // Creem checkout URL
  products: {
    pro: {
      id: 'prod_10w6tRy1qlvUVAkyiDgX7E', // SleepCalc Pro (月付) - $4.99/month
      name: 'SleepCalc Pro',
      price: '$4.99/month',
      description: 'Unlock advanced sleep optimization features'
    },
    premium: {
      id: 'prod_2mKSW1peZ6P7A1MpZNlcdc', // SleepCalc Pro Annual (年付) - $39.00/year
      name: 'SleepCalc Pro — Annual',  
      price: '$39.00/year',
      description: 'Complete sleep mastery with all premium features'
    }
  },
  successUrl: 'https://sleepcalc-pro.pages.dev/?payment=success',
  cancelUrl: 'https://sleepcalc-pro.pages.dev/?payment=cancel'
};

export function CreemPaymentButton({ 
  plan, 
  className = '', 
  variant = 'primary',
  size = 'md',
  disabled = false 
}: CreemPaymentButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useUIStore();
  
  const product = CREEM_CONFIG.products[plan];

  // 预先构建 href，使用 <a> 导航，避免某些环境对 window.open/location 的限制
  const href = useMemo(() => {
    try {
      const userId = getUserId();
      return `${CREEM_CONFIG.baseUrl}/payment/${product.id}?customer_id=${encodeURIComponent(userId)}&success_url=${encodeURIComponent(CREEM_CONFIG.successUrl)}&cancel_url=${encodeURIComponent(CREEM_CONFIG.cancelUrl)}`;
    } catch {
      return `${CREEM_CONFIG.baseUrl}/payment/${product.id}`;
    }
  }, [product.id]);
  
  // 样式配置
  const baseStyles = 'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantStyles = {
    primary: plan === 'premium' 
      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl focus:ring-purple-500'
      : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl focus:ring-blue-500',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 focus:ring-slate-500',
    outline: `border-2 ${plan === 'premium' ? 'border-purple-600 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/10 focus:ring-purple-500' : 'border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10 focus:ring-blue-500'}`
  };
  
  const sizeStyles = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-6 py-4 text-lg'
  };
  
  const handlePayment = async () => {
    console.log('支付按钮被点击', { plan, disabled, isLoading });
    
    if (disabled || isLoading) return;
    
    setIsLoading(true);
    
    try {
      const userId = getUserId();
      console.log('获取到用户ID:', userId);
      
      // 构建Creem支付URL - 使用正确的格式
      const paymentUrl = `${CREEM_CONFIG.baseUrl}/payment/${product.id}?customer_id=${encodeURIComponent(userId)}&success_url=${encodeURIComponent(CREEM_CONFIG.successUrl)}&cancel_url=${encodeURIComponent(CREEM_CONFIG.cancelUrl)}`;

      console.log('跳转到支付页面:', paymentUrl);

      // 多策略导航，最大化兼容性
      try {
        // 优先使用 assign（不会污染历史的替换可改成 replace）
        window.location.assign(paymentUrl);
        return;
      } catch (e) {
        console.warn('location.assign 失败，尝试 window.open(_self):', e);
      }

      try {
        const w = window.open(paymentUrl, '_self');
        if (w) return;
        console.warn('window.open 返回 null，回退到 a.click');
      } catch (e) {
        console.warn('window.open 失败，回退到 a.click:', e);
      }

      try {
        const a = document.createElement('a');
        a.href = paymentUrl;
        a.rel = 'noreferrer noopener';
        a.target = '_self';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      } catch (e) {
        console.error('所有跳转方式均失败:', e);
        throw e;
      }
      
    } catch (error) {
      console.error('支付跳转失败:', error);
      
      addNotification({
        type: 'error',
        message: 'Payment initialization failed. Please try again.',
        duration: 5000
      });
      
      setIsLoading(false);
    }
  };
  
  const getIcon = () => {
    if (isLoading) {
      return <Loader2 className="w-5 h-5 animate-spin" />;
    }
    
    if (plan === 'premium') {
      return <Crown className="w-5 h-5" />;
    }
    
    return variant === 'outline' ? <CreditCard className="w-5 h-5" /> : <Zap className="w-5 h-5" />;
  };
  
  const buttonText = isLoading 
    ? 'Redirecting...' 
    : `Upgrade to ${product.name}`;
  
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={`relative z-[60] pointer-events-auto ${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      whileHover={!disabled && !isLoading ? { scale: 1.02 } : undefined}
      whileTap={!disabled && !isLoading ? { scale: 0.98 } : undefined}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {getIcon()}
      <span>{buttonText}</span>
      
      {!isLoading && (
        <span className="text-sm opacity-75">
          {product.price}
        </span>
      )}
    </motion.a>
  );
}

// 快速升级卡片组件
interface UpgradeCardProps {
  plan: 'pro' | 'premium';
  featured?: boolean;
  className?: string;
}

export function UpgradeCard({ plan, featured = false, className = '' }: UpgradeCardProps) {
  const product = CREEM_CONFIG.products[plan];
  
  const features = {
    pro: [
      'Advanced AI assistant',
      'Sleep analytics dashboard', 
      'Custom sleep plans',
      'Device integrations',
      'Smart notifications',
      'Priority support'
    ],
    premium: [
      'All Pro features',
      'Advanced moon tracking',
      'Sleep coaching sessions',
      'Custom scenarios',
      'Export data',
      'API access',
      'White-label options'
    ]
  };
  
  return (
    <motion.div
      className={`relative p-6 rounded-2xl border ${
        featured 
          ? 'border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/10 dark:to-indigo-900/10' 
          : 'border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700'
      } ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {featured && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Most Popular
          </span>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          {product.name}
        </h3>
        <div className="text-4xl font-bold text-slate-900 dark:text-white mb-1">
          {product.price.split('/')[0]}
        </div>
        <div className="text-slate-600 dark:text-slate-400">
          per {product.price.includes('month') ? 'month' : 'year'}
        </div>
      </div>
      
      <p className="text-slate-600 dark:text-slate-400 text-center mb-6">
        {product.description}
      </p>
      
      <ul className="space-y-3 mb-8">
        {features[plan].map((feature, index) => (
          <li key={index} className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${
              plan === 'premium' 
                ? 'bg-purple-500' 
                : 'bg-blue-500'
            }`} />
            <span className="text-slate-700 dark:text-slate-300">
              {feature}
            </span>
          </li>
        ))}
      </ul>
      
      <CreemPaymentButton
        plan={plan}
        variant={featured ? 'primary' : 'outline'}
        size="lg"
        className="w-full"
      />
    </motion.div>
  );
}
